import { useEffect, useRef, useState } from 'react'
import { serverUrl } from '../../../../../utilites/config'
import { reconnectingSocket } from '../../../../../utilites/socket'

export interface BurstStatus {
  isActive: boolean
  startTimestamp: number | null
}

const NO_BURST: BurstStatus = {
  isActive: false,
  startTimestamp: null,
}

/**
 * Subscribes to the burst-status topic of the band currently on screen and reports the
 * latest status. Resubscribes whenever the active band changes and closes the connection
 * on unmount.
 *
 * @param activeTab - Index of the selected band tab (0 = F10.7, anything else = F30)
 * @param logLabel - Component name used to tag malformed-payload errors in the console
 * @returns The most recent burst status, or an inactive one before the first message
 */
export function useBurstStatus(activeTab: number, logLabel: string): BurstStatus {
  const connectionRef = useRef<any>(null)
  // eslint-disable-next-line no-unused-vars
  const handlerRef = useRef<((d: any) => void) | null>(null)
  const [burstStatus, setBurstStatus] = useState<BurstStatus>(NO_BURST)

  useEffect(() => {
    const handleBurstStatus = (d: any) => {
      try {
        const status = JSON.parse(d.body)
        if (status && typeof status.active === 'boolean') {
          setBurstStatus({
            isActive: status.active,
            startTimestamp: status.startTimestamp || null,
          })
        }
      } catch (error) {
        console.error(`[${logLabel}] Error processing burst status:`, error)
      }
    }

    // Indirection through a ref keeps the subscription stable while the handler is recreated.
    handlerRef.current = handleBurstStatus
    const handleStatus = (d: any) => handlerRef.current?.(d)

    connectionRef.current = reconnectingSocket(
      serverUrl + '/websocket',
      handleStatus,
      `/topic/burst-status-${activeTab === 0 ? '107' : '30'}`,
      window.location.href,
    )

    return () => {
      const conn = connectionRef.current
      if (conn) {
        conn.disconnect?.()
      }
      connectionRef.current = null
    }
  }, [activeTab, logLabel])

  return burstStatus
}
